/**
 * Hook that mirrors PlantHOC's data-loading behavior for a given site
 * group id: seeds the Redux plant context, fetches the site-group entity
 * (with 5-minute client-side cache), and loads feature associations and
 * the site-group curfew.
 *
 * Supports the `skipEntityFetches` flag used by dashboard views that seed
 * their entity store from the odl-app snapshot instead of individual API
 * calls.
 *
 * Contains:
 * - useSiteGroup — fetches and returns the selected site group + loading state
 */
import { isAfter, parseISO, subMinutes } from 'date-fns';
// eslint-disable-next-line no-restricted-imports -- pre-existing Immutable usage; migrate off when touched
import { Map } from 'immutable';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchSiteGroup } from '@sophocles/entities/SiteGroup';
import { loadSiteGroupFeatureAssociations } from '@sophocles/features/featureFlags/api';
import { setEntityData } from '@sophocles/features/resources/resourceHandlerActions';
import { getEntityDataSelector } from '@sophocles/features/resources/resourceHandlerSelectors';
import { loadSiteGroupCurfewForSiteGroup } from '@sophocles/features/siteGroups/api';
import { selectSiteGroupCurfewBySiteGroupId } from '@sophocles/features/siteGroups/slice';
import { setPlant } from '@sophocles/features/sites/slices/selectedPlant/actions';
import { getSelectedPlantSelector, selectedPlant } from '@sophocles/features/sites/slices/selectedPlant/selectors';

/**
 * Fetch site-group data and seed the plant Redux context.
 *
 * @param {string | number} siteGroupId      URL param value (sitegroup_id)
 * @param {{ skipEntityFetches?: boolean }} [options]
 * @returns {{ siteGroup: import('immutable').Map, loading: boolean, error: boolean, curfew: *, reload: () => void }}
 */
export default function useSiteGroup(siteGroupId, { skipEntityFetches = false } = {}) {
  const dispatch = useDispatch();
  const selectedPlantId = useSelector(selectedPlant);
  const siteGroup = useSelector(getSelectedPlantSelector);
  const entityData = useSelector(getEntityDataSelector);
  const curfew = useSelector(selectSiteGroupCurfewBySiteGroupId(selectedPlantId));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  // Bumped by reload() to re-run the seed effect after a failed fetch.
  const [reloadToken, setReloadToken] = useState(0);
  const reload = useCallback(() => setReloadToken(token => token + 1), []);

  // Track which site group we've loaded feature associations + curfew for.
  const lastLoadedRef = useRef(null);

  // Keep refs to entityData and curfew so the effect can read the latest
  // cache state without listing them as deps (which would cause the effect
  // to re-run on every store update — mirroring the HOC's
  // componentDidUpdate-on-param-change behavior).
  const entityDataRef = useRef(entityData);
  entityDataRef.current = entityData;

  const curfewRef = useRef(curfew);
  curfewRef.current = curfew;

  useEffect(() => {
    if (!siteGroupId) return;

    // Always seed the plant context — this is cheap and needed for
    // selectors downstream even when skipEntityFetches is set.
    dispatch(setPlant(siteGroupId));

    if (!skipEntityFetches) {
      const currentEntityData = entityDataRef.current;
      const fiveMinutesAgo = subMinutes(new Date(), 5);
      const meta = currentEntityData.get('Plant', Map());
      const isStale =
        meta.isEmpty() ||
        meta.get('id', 0) != siteGroupId ||
        isAfter(fiveMinutesAgo, parseISO(meta.get('timestamp', new Date().toISOString())));

      if (isStale) {
        setLoading(true);
        setError(false);
        dispatch(fetchSiteGroup(siteGroupId))
          .then(() => dispatch(setEntityData('Plant', { id: siteGroupId })))
          .then(() => setLoading(false))
          .catch(() => {
            setLoading(false);
            setError(true);
          });
      }
    }

    // Feature associations and curfew are per-site-group.
    if (lastLoadedRef.current !== siteGroupId) {
      lastLoadedRef.current = siteGroupId;

      if (!skipEntityFetches) {
        dispatch(loadSiteGroupFeatureAssociations(siteGroupId));

        if (curfewRef.current == null) {
          dispatch(loadSiteGroupCurfewForSiteGroup(siteGroupId));
        }
      }
    }
  }, [siteGroupId, skipEntityFetches, dispatch, reloadToken]);

  return { siteGroup, loading, error, curfew, reload };
}
